import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, trendingVideos, categoryVideos } from '@/mocks/homeData';

const allCategoryVideos = categories.reduce<Record<string, { id: string; title: string; rating: number; views: string; category: string; categoryName: string; duration: string; year: number; description: string; imageUrl: string }[]>>((acc, cat) => {
  const catVids = categoryVideos[cat.slug] || [];
  acc[cat.slug] = catVids.map((v, i) => ({
    ...v,
    category: cat.slug,
    categoryName: cat.name,
    duration: cat.slug === 'tv' ? `${24 + i * 12}集` : '1h 30min',
    year: 2026 - i,
    description: `${v.title} - 糖心视频高清免费在线观看，不缓冲流畅播放。${cat.name}分类热门推荐内容。`,
  }));
  const trendingForCat = trendingVideos.filter(tv => tv.category === cat.slug).slice(0, 4);
  acc[cat.slug] = [...acc[cat.slug], ...trendingForCat.map(tv => ({
    id: tv.id,
    title: tv.title,
    rating: tv.rating,
    views: tv.views,
    category: tv.category,
    categoryName: tv.categoryName,
    duration: tv.duration,
    year: tv.year,
    description: tv.description,
    imageUrl: tv.imageUrl,
  }))];
  return acc;
}, {});

const sortOptions = [
  { value: 'popular', label: '最热门' },
  { value: 'newest', label: '最新' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('popular');
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find(c => c.slug === activeSlug);
  const videos = allCategoryVideos[activeSlug] || [];

  const sortedVideos = useMemo(() => {
    const list = [...videos];
    if (sortBy === 'popular') {
      return list.sort((a, b) => parseFloat(b.views.replace('万', '')) - parseFloat(a.views.replace('万', '')));
    }
    if (sortBy === 'newest') {
      return list.sort((a, b) => b.year - a.year);
    }
    if (sortBy === 'rating') {
      return list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [videos, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${currentCategory?.name} - 糖心视频`,
            "description": currentCategory?.description,
            "url": `https://tangxin.com/category/${activeSlug}`,
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://tangxin.com" },
                { "@type": "ListItem", "position": 2, "name": currentCategory?.name }
              ]
            }
          })
        }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl">
                <i className={`${currentCategory?.icon || 'ri-film-line'} text-background-50 text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory?.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory?.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                  onClick={() => setActiveSlug(cat.slug)}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <strong className="text-sm text-foreground-500 mt-1">
                共 <span className="text-foreground-900 font-semibold">{sortedVideos.length}</span> 部作品
              </strong>
              <div className="flex items-center gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {sortedVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/category/${activeSlug}`}
                  className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={video.imageUrl}
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      title={`${video.title} - 糖心视频${currentCategory?.name}在线观看`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
                        {video.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3">
                      <span className="bg-background-950/70 text-background-50 text-xs px-2 py-0.5 rounded-md backdrop-blur-sm">
                        {video.duration}
                      </span>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary-500/90 rounded-full">
                        <i className="ri-play-fill text-background-50 text-xl"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-2">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{video.year}</span>
                      <span className="text-foreground-300">·</span>
                      <span>{video.views}次播放</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}